import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { State } from '../../types/state'
import { useRequests } from '../../requests/useRequests'
import { RefreshActions } from '../../state/refresh'
import { Link } from 'react-router-dom'
import { Formik, Form, ErrorMessage, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import { SideMenu } from '../Header/SiteMenu'
import { isMinimumPackage } from '../../utils/package-validator'
import { Package } from '../../enums/package.enum'
import AppNavBar from '../Header/AppNavBar'
import ButtonLink from '../Buttons/ButtonLink'

// Typ dla wartości formularza
interface UserFormValues {
  username: string
  email: string
  phoneNumber: string
  street: string
  city: string
  zipCode: string
}

const UserProfile = () => {
  const dispatch = useDispatch()
  const requestMaker = useRequests()
  const userState = useSelector((state: State) => state.user)
  const { username, email, phoneNumber, street, city, zipCode, package: userPackage, packageExpiredAt } = userState

  // Stany do obsługi formularza
  const [isEditing, setIsEditing] = useState(false)

  // Stany do potwierdzania emaila
  const [isEmailCodeVisible, setIsEmailCodeVisible] = useState(false)
  const [emailConfirmationCode, setEmailConfirmationCode] = useState('')
  const [emailConfirmationCodeError, setEmailConfirmationCodeError] = useState<string | null>(null)

  // Schema walidacji formularza
  const validationSchema = Yup.object({
    username: Yup.string().required('Nazwa użytkownika jest wymagana'),
    email: Yup.string()
      .email('Nieprawidłowy format adresu email')
      .required('Email jest wymagany'),
    phoneNumber: Yup.string()
  })

  // Początkowe wartości formularza
  const initialValues: UserFormValues = {
    username: userState.username || '',
    email: userState.email || '',
    phoneNumber: userState.phoneNumber || '',
    street: userState.street || '',
    city: userState.city || '',
    zipCode: userState.zipCode || ''
  }

  // Alternatywnie, aktualizacja stanów przy wejściu w tryb edycji
  const enableEditing = () => {
    setIsEditing(!isEditing)
  }

  // Obsługa edycji danych użytkownika
  const handleSubmit = async (values: UserFormValues, { setSubmitting, setFieldError }: FormikHelpers<UserFormValues>) => {
    try {
      await requestMaker.updateUserDetails({
        username: values.username,
        email: values.email,
        phoneNumber: values.phoneNumber,
        street: values.street,
        city: values.city,
        zipCode: values.zipCode
      } as any)

      dispatch(
        RefreshActions.setToastMessage({
          message: 'Dane użytkownika zostały zaktualizowane pomyślnie'
        })
      )
      dispatch(RefreshActions.refreshUserState())
      setIsEditing(false)
    } catch (error) {
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Wystąpił błąd podczas aktualizacji danych'
          // severity: 'error'
        })
      )
    } finally {
      setSubmitting(false)
    }
  }

  // Funkcje do potwierdzania emaila
  const handleSendEmailCode = async () => {
    setIsEmailCodeVisible(true)
    try {
      await requestMaker.sendEmailCode()
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Kod weryfikacyjny został wysłany na Twój email'
        })
      )
    } catch (error) {
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się wysłać kodu weryfikacyjnego'
          // severity: 'error'
        })
      )
    }
  }

  const handleActivateEmail = async () => {
    try {
      await requestMaker.activateEmail(emailConfirmationCode)
      setIsEmailCodeVisible(false)
      setEmailConfirmationCodeError(null)
      dispatch(RefreshActions.refreshUserState())
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Email został pomyślnie zweryfikowany'
        })
      )
    } catch (e) {
      setEmailConfirmationCodeError('Nieprawidłowy kod potwierdzający')
    }
  }

  // Sprawdzanie czy email/username już istnieje
  const checkUsernameExists = async (value: string, setFieldError: (field: string, message: string | undefined) => void) => {
    if (value === userState.username) return

    try {
      const response = await requestMaker.emailOrUsernameExists(value)
      if (response.data) {
        setFieldError('username', 'Użytkownik o tej nazwie już istnieje')
      }
    } catch (error) {
      console.error('Błąd podczas sprawdzania nazwy użytkownika', error)
    }
  }

  const checkEmailExists = async (value: string, setFieldError: (field: string, message: string | undefined) => void) => {
    if (value === userState.email) return

    try {
      const response = await requestMaker.emailExists(value)
      if (response.data) {
        setFieldError('email', 'Ten adres email jest już używany')
      }
    } catch (error) {
      console.error('Błąd podczas sprawdzania adresu email', error)
    }
  }

  return (
    <>
      <AppNavBar
        rightIcons={[]}
      />
      <div style={{ display: 'flex', minHeight: 'calc(100vh - 80px)' }}>
        <SideMenu />
        <section className='user-details pt-40 pb-40' style={{ flex: 1, overflow: 'auto', background: '#f8f9fa', padding: '0 24px' }}>
          <div className='container'>
            <div className='row'>
              <div className='col-sm-12 col-md-12 col-lg-4'>
                <div className='profile-card text-center p-30 mb-30'>
                  <h4 className='profile-name mb-10'>{userState.username || 'Użytkownik'}</h4>
                  <p className='profile-bio mb-20'>Twój osobisty profil w systemie Kreatlo</p>
                  <button
                    className={`
                        tw-w-full tw-mb-4 tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                        tw-text-white
                        tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base
                        ${isEditing
                          ? 'tw-bg-red-500 hover:tw-bg-opacity-90 focus:tw-bg-red-500'
                          : 'tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green'
                         }
                      `}
                    onClick={enableEditing}
                  >
                    {isEditing ? 'Anuluj edycję' : 'Edytuj profil'}
                  </button>
                  <ButtonLink
                    to='/change-password'
                    className='tw-w-full tw-flex tw-justify-center tw-text-center tw-mb-4 tw-bg-white'
                    style={{
                      color: '#0e2b3d'
                    }}
                  >
                    Zmień hasło
                  </ButtonLink>

                  {isMinimumPackage(userPackage, Package.START, packageExpiredAt) && (
                    <ButtonLink
                      to='/designer/edit'
                      className='tw-w-full tw-flex tw-justify-center tw-text-center tw-mb-4 tw-bg-white'
                      style={{
                        color: '#0e2b3d'
                      }}
                    >
                      Wizytówka projektanta
                    </ButtonLink>
                  )}
                </div>

                {/* Status weryfikacji */}
                <div className='verification-status p-30 mb-30'>
                  <h3 className='mb-20'>Status weryfikacji</h3>
                  <div className='d-flex align-items-center mb-10'>
                    <i className={`fa ${userState.isEmailConfirmed ? 'fa-check-circle text-success' : 'fa-times-circle text-danger'} mr-10`} style={{ fontSize: '18px' }} />
                    <span>
                      <strong>Email:</strong> {userState.isEmailConfirmed ? 'Zweryfikowany' : 'Niezweryfikowany'}
                    </span>
                  </div>
                  <div className='d-flex align-items-center mb-10'>
                    <i className={`fa ${userState.isPhoneNumberConfirmed ? 'fa-check-circle text-success' : 'fa-times-circle text-danger'} mr-10`} style={{ fontSize: '18px' }} />
                    <span>
                      <strong>Telefon:</strong> {userState.isPhoneNumberConfirmed ? 'Zweryfikowany' : 'Niezweryfikowany'}
                    </span>
                  </div>

                  {/* Przyciski weryfikacji */}
                  {!userState.isEmailConfirmed && (
                    <div className='mt-20'>
                      <button
                        className={`
                          tw-w-full tw-mb-4 tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                          tw-text-white
                          tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base
                          tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                        `}
                        onClick={handleSendEmailCode}
                      >
                        Wyślij kod weryfikacyjny email
                      </button>

                      {isEmailCodeVisible && (
                        <div className='mt-20'>
                          <div className='form-group'>
                            <input
                              type='text'
                              className={`form-control ${emailConfirmationCodeError ? 'is-invalid' : ''}`}
                              placeholder='Wprowadź kod z email'
                              value={emailConfirmationCode}
                              onChange={(e) => setEmailConfirmationCode(e.target.value)}
                            />
                            {emailConfirmationCodeError && (
                              <div className='invalid-feedback'>{emailConfirmationCodeError}</div>
                            )}
                          </div>
                          <button
                            className={`
                              tw-w-full tw-mb-4 tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                              tw-text-white
                              tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base
                              tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                            `}
                            onClick={handleActivateEmail}
                          >
                            Potwierdź email
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className='col-sm-12 col-md-12 col-lg-8'>
                <div className='profile-details p-30 mb-30'>
                  <h3 className='profile-details-title mb-20 tw-font-semibold'>Informacje o koncie</h3>

                  <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                  >
                    {({ values, errors, touched, handleChange, handleBlur, setFieldError, isSubmitting }) => (
                      <Form>
                        <div className='row'>
                          <div className='col-sm-12 col-md-6'>
                            <div className='form-group mb-20'>
                              <label className='form-label'>Nazwa użytkownika</label>
                              <input
                                type='text'
                                name='username'
                                className={`form-control ${errors.username && touched.username ? 'is-invalid' : ''}`}
                                value={isEditing ? values.username : userState.username || ''}
                                onChange={handleChange}
                                onBlur={(e) => {
                                  handleBlur(e)
                                  isEditing && checkUsernameExists(e.target.value, setFieldError)
                                }}
                                readOnly={!isEditing}
                                disabled={!isEditing}
                              />
                              <ErrorMessage name='username' component='div' className='invalid-feedback' />
                            </div>
                          </div>
                          <div className='col-sm-12 col-md-6'>
                            <div className='form-group mb-20'>
                              <label className='form-label'>Email</label>
                              <input
                                type='email'
                                name='email'
                                className={`form-control ${errors.email && touched.email ? 'is-invalid' : ''}`}
                                value={isEditing ? values.email : userState.email || ''}
                                onChange={handleChange}
                                onBlur={(e) => {
                                  handleBlur(e)
                                  isEditing && checkEmailExists(e.target.value, setFieldError)
                                }}
                                readOnly={!isEditing}
                                disabled={!isEditing}
                              />
                              <ErrorMessage name='email' component='div' className='invalid-feedback' />
                            </div>
                          </div>
                          <div className='col-sm-12'>
                            <div className='form-group mb-20'>
                              <label className='form-label'>Numer telefonu</label>
                              <input
                                type='tel'
                                name='phoneNumber'
                                className={`form-control ${errors.phoneNumber && touched.phoneNumber ? 'is-invalid' : ''}`}
                                value={isEditing ? values.phoneNumber : userState.phoneNumber || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder='Wprowadź numer telefonu'
                                readOnly={!isEditing}
                                disabled={!isEditing}
                              />
                              <ErrorMessage name='phoneNumber' component='div' className='invalid-feedback' />
                            </div>
                          </div>
                          <div className='col-sm-12'>
                            <h5 className='mb-3 tw-font-semibold'>Adres</h5>
                          </div>
                          <div className='col-sm-12 col-md-4'>
                            <div className='form-group mb-20'>
                              <label className='form-label'>Ulica</label>
                              <input
                                type='text'
                                name='street'
                                className='form-control'
                                value={isEditing ? values.street : userState.street || ''}
                                onChange={handleChange}
                                placeholder='Nazwa ulicy i numer'
                                readOnly={!isEditing}
                                disabled={!isEditing}
                              />
                            </div>
                          </div>
                          <div className='col-sm-12 col-md-4'>
                            <div className='form-group mb-20'>
                              <label className='form-label'>Miasto</label>
                              <input
                                type='text'
                                name='city'
                                className='form-control'
                                value={isEditing ? values.city : userState.city || ''}
                                onChange={handleChange}
                                placeholder='Miasto'
                                readOnly={!isEditing}
                                disabled={!isEditing}
                              />
                            </div>
                          </div>
                          <div className='col-sm-12 col-md-4'>
                            <div className='form-group mb-20'>
                              <label className='form-label'>Kod pocztowy</label>
                              <input
                                type='text'
                                name='zipCode'
                                className='form-control'
                                value={isEditing ? values.zipCode : userState.zipCode || ''}
                                onChange={handleChange}
                                placeholder='Kod pocztowy'
                                readOnly={!isEditing}
                                disabled={!isEditing}
                              />
                            </div>
                          </div>

                          {isEditing && (
                            <div className='col-sm-12 text-center mt-20'>
                              <button
                                type='submit'
                                className={`
                                      tw-w-full tw-mb-4 tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                      tw-text-white
                                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                                    `}
                                disabled={isSubmitting}
                              >
                                Zapisz zmiany
                              </button>
                            </div>
                          )}
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>

                <div className='profile-activity p-30 mb-30'>
                  <h3 className='profile-activity-title mb-20 tw-font-semibold'>Aktywność konta</h3>

                  {/* Dodanie informacji o pakiecie */}
                  <div className='activity-item d-flex align-items-center mb-10 pb-10' style={{ borderBottom: '1px solid #eee' }}>
                    <div className='activity-icon mr-20'>
                      <i className='fa fa-gem' style={{ fontSize: '24px', color: '#0e2b5c' }} />
                    </div>
                    <div className='activity-info'>
                      <h5 className='activity-title mb-5'>Aktualny pakiet</h5>
                      <p className='activity-desc mb-0'>
                        {userState.package
                          ? `${userState.package} - ważny do: ${(userState.packageExpiredAt != null)
                            ? new Date(userState.packageExpiredAt).toLocaleDateString()
                            : 'bezterminowo'}`
                          : 'Brak aktywnego pakietu'}
                      </p>
                      {userState.package && userState.packageUsersNumber && (
                        <p className='activity-desc text-info mt-2 mb-0'>
                          <i className='fa fa-users mr-1' /> Liczba użytkowników: <strong>{userState.packageUsersNumber}</strong>
                        </p>
                      )}
                      {userState.hadPremiumTrial && (
                        <p className='activity-desc text-muted mt-2 mb-0'>
                          <i className='fa fa-check-circle mr-1' /> Wykorzystałeś już swoją wersję próbną pakietu Premium
                        </p>
                      )}
                    </div>
                  </div>

                  <div className='activity-item d-flex align-items-center mb-10 pb-10' style={{ borderBottom: '1px solid #eee' }}>
                    <div className='activity-icon mr-20'>
                      <i className='fa fa-shopping-cart' style={{ fontSize: '24px', color: '#0e2b5c' }} />
                    </div>
                    <div className='activity-info'>
                      <h5 className='activity-title mb-5'>Ostatni zakup</h5>
                      <p className='activity-desc mb-0'>Plan Podstawowy - 12.04.2023</p>
                    </div>
                  </div>

                  {/* Dodanie wyświetlania statusu administratora w profilu */}
                  <div className='activity-item d-flex align-items-center mb-10 pb-10' style={{ borderBottom: '1px solid #eee' }}>
                    <div className='activity-icon mr-20'>
                      <i className='fa fa-user-shield' style={{ fontSize: '24px', color: '#0e2b5c' }} />
                    </div>
                    <div className='activity-info'>
                      <h5 className='activity-title mb-5'>Uprawnienia</h5>
                      <p className='activity-desc mb-0'>
                        {userState.isSuperadmin
                          ? 'Administrator systemu'
                          : 'Standardowe uprawnienia użytkownika'}
                      </p>
                    </div>
                  </div>

                  {/* <div className='activity-item d-flex align-items-center mb-10 pb-10' style={{ borderBottom: '1px solid #eee' }}>
                    <div className='activity-icon mr-20'>
                      <i className='fa fa-credit-card' style={{ fontSize: '24px', color: '#0e2b5c' }} />
                    </div>
                    <div className='activity-info'>
                      <h5 className='activity-title mb-5'>Płatności</h5>
                      <p className='activity-desc mb-0'>
                        <Link to='/payment-history' className='text-primary'>
                          Zobacz historię płatności <i className='fa fa-arrow-right ml-1' />
                        </Link>
                      </p>
                    </div>
                  </div> */}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

export default UserProfile
